import { z } from 'zod';
import { ProfessionalInfo } from '../../Professional';
import { payments } from '../../../data';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import { ReferralStoredDocumentsSchema } from '../ReferralDocumentsSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
  requiresPatientConsentEvidence,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { ReferrerSchema } from '../ReferrerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

// Optional triage metadata. The referrer selects a service on the form, but
// every default referral is routed to the Cancer Nurse Specialist, so we keep
// the originally requested service here for triage reference.
const ReferralMetadataSchema = z
  .object({
    requestedServiceId: z.string().nullish(),
    requestedServiceName: z.string().nullish(),
  })
  .optional();

const paymentSchema = z
  .string()
  .refine((payment) => payments.includes(payment));

// Used to create a referral from the website UI
export const ReferralCreateDefaultSchema = (
  serviceIds: string[],
  professionals: Record<string, ProfessionalInfo>,
) => {
  return withLegacyPlanIdentifiers(
    z
      .object({
        patient: ReferralPatientSchema,
        coverage: ReferralCoverageSchema,
        diagnosis: ReferralDiagnosisSchema,
        payment: paymentSchema,
        referrer: ReferrerSchema.extend({
          id: z.string().optional(),
        }),
        referral: z
          .object({
            serviceId: z
              .string()
              .refine((serviceId) => serviceIds.includes(serviceId)),
            hasPreferredProfessional: z.boolean(),
            preferredProfessionalId: z.string().optional(),
          })
          // The professional only matters when the referrer said they have a
          // preference: then one must be chosen, and it must be a professional
          // who offers the service chosen in the previous field
          .check((ctx) => {
            const {
              hasPreferredProfessional,
              preferredProfessionalId,
              serviceId,
            } = ctx.value;

            if (!hasPreferredProfessional) {
              return;
            }

            if (!preferredProfessionalId) {
              ctx.issues.push({
                code: 'custom',
                input: ctx.value,
                path: ['preferredProfessionalId'],
                message:
                  'Preferred professional is required when selected "Yes" in the previous field',
              });
              return;
            }

            if (
              !professionals[preferredProfessionalId]?.services.includes(
                serviceId,
              )
            ) {
              ctx.issues.push({
                code: 'custom',
                input: ctx.value,
                path: ['preferredProfessionalId'],
                message:
                  'Preferred professional should be selected from the available list',
              });
            }
          }),
        metadata: ReferralMetadataSchema,
        // Optional free text the referrer typed on the referral form. Internal
        // only: it is never shown to the member (see ReferralNotesSchema)
        notes: ReferralNotesSchema,
        // Documents attached to the referral, sent as the canonical `documents`
        // array of the body (PPL-3549)
        documents: ReferralStoredDocumentsSchema,
        // How we know the patient consented to being referred. The REQUEST form:
        // a body states the EVIDENCE only — the status and what we record when we
        // ask are ours to write (see ReferralConsentSchema; the stored schema
        // below carries the full shape)
        consent: ReferralConsentRequestSchema,
      })
      .check(requiresPatientPhoneForConsent)
      .check(requiresPatientConsentEvidence),
  );
};

const EmptyInputReferralSchema = ReferralCreateDefaultSchema([], {});
export type CreateDeafaultReferral = z.infer<typeof EmptyInputReferralSchema>;

// Referral as it is stored in the database
export const ReferralDefaultSchema = ReferralBaseSchema.extend({
  patient: ReferralPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  payment: paymentSchema,
  referrer: ReferrerSchema,
  metadata: ReferralMetadataSchema,
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
  documents: ReferralStoredDocumentsSchema,

  preferredProfessional: z
    .object({
      id: z.string().min(1),
      fullname: z.string().min(1),
      services: z.array(z.string().min(1)),
      /** Legacy slug-based identifier for the Healee booking URL */
      legacyId: z.string().nullish(),
    })
    .optional(),
});

export type ReferralDefaultData = z.infer<typeof ReferralDefaultSchema>;
export type ReferralDefault = IObject.HasIdTimestamped<ReferralDefaultData>;
